import { CartUndoData } from "@/types/cart";

/**
 * Centralized Cart Storage Utility
 *
 * Usage:
 * - Use sessionStorage for data that should be cleared when browser closes (upgraded items, removed items)
 * - Use localStorage for persistent data that should survive browser sessions (upsell tracking)
 */

/**
 * Cart Storage Keys - centralized constants
 * All keys must be prefixed with "cart_"
 */
export const CART_STORAGE_KEYS = {
  // sessionStorage
  UPGRADED_ITEMS: "cart_upgradedItems",
  REMOVED_ITEM: "cart_removedItem",
  SCALP_UPSELL_ADDED: "cart_scalpUpsellAdded",
  UPSELL_SHOWN_TRACKED: "cart_upsellShownTracked",
  /** Theme of last non-empty cart; used to style EmptyCart until items are added again */
  LAST_THEME: "cart_lastTheme",
  CART_UNDO_DATA: "cart_undoData", // sessionStorage
  /** HGN lines the GLP-1 cart upsell replaced, so its Undo can put them back as they were */
  GLP1_ORIGINAL_LINES: "cart_glp1OriginalLines",

  // localStorage
  CART: "cart",
  SCALP_LP_UPGRADED_SKUS: "cart_scalpLpUpgradedSkus",
  SCALP_ROUTINE_LP_UPGRADED_SKUS: "cart_scalpRoutineLpUpgradedSkus",
  SCALP_UPGRADED_SKUS: "cart_scalpUpgradedSkus",
  INFLUENCER_UPSELL_SKUS: "cart_influencerUpsellSkus",
  SCALP_ACCOUNT_UPGRADED_SKUS: "cart_scalpAccountUpgradedSkus",
  PDP_UPGRADED_SKU: "cart_pdpUpgradedSku",
} as const;

export type CartStorageKey = (typeof CART_STORAGE_KEYS)[keyof typeof CART_STORAGE_KEYS];

/**
 * Type-safe storage getter with JSON parsing
 * @param key - The storage key from CART_STORAGE_KEYS
 * @param storage - "session" or "local" (defaults to "session")
 * @returns Parsed value or null if not found/error
 */
export function getCartStorage<T>(
  key: CartStorageKey,
  storage: "session" | "local" = "session"
): T | null {
  try {
    if (typeof window === "undefined") return null;
    const storageObj = storage === "session" ? sessionStorage : localStorage;
    const value = storageObj.getItem(key);
    if (value === null || value === "undefined") return null;
    return JSON.parse(value) as T;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Failed to get ${key} from ${storage}Storage:`, error);
    return null;
  }
}

/**
 * Type-safe storage setter with JSON stringify
 * @param key - The storage key from CART_STORAGE_KEYS
 * @param value - Value to store (will be JSON stringified)
 * @param storage - "session" or "local" (defaults to "session")
 * @returns true if successful, false otherwise
 */
export function setCartStorage<T>(
  key: CartStorageKey,
  value: T,
  storage: "session" | "local" = "session"
): boolean {
  try {
    if (typeof window === "undefined") return false;
    const storageObj = storage === "session" ? sessionStorage : localStorage;
    storageObj.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Failed to set ${key} in ${storage}Storage:`, error);
    return false;
  }
}

/**
 * Remove item from storage
 * @param key - The storage key from CART_STORAGE_KEYS
 * @param storage - "session" or "local" (defaults to "session")
 */
export function removeCartStorage(
  key: CartStorageKey,
  storage: "session" | "local" = "session"
): void {
  try {
    if (typeof window === "undefined") return;
    const storageObj = storage === "session" ? sessionStorage : localStorage;
    storageObj.removeItem(key);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Failed to remove ${key} from ${storage}Storage:`, error);
  }
}

/** Session keys (cleared on order success). */
const SESSION_KEYS: CartStorageKey[] = [
  CART_STORAGE_KEYS.UPGRADED_ITEMS,
  CART_STORAGE_KEYS.REMOVED_ITEM,
  CART_STORAGE_KEYS.SCALP_UPSELL_ADDED,
  CART_STORAGE_KEYS.UPSELL_SHOWN_TRACKED,
  CART_STORAGE_KEYS.LAST_THEME,
  CART_STORAGE_KEYS.GLP1_ORIGINAL_LINES,
];

/** Local keys (cleared on order success). */
const LOCAL_KEYS: CartStorageKey[] = [
  CART_STORAGE_KEYS.SCALP_LP_UPGRADED_SKUS,
  CART_STORAGE_KEYS.SCALP_ROUTINE_LP_UPGRADED_SKUS,
  CART_STORAGE_KEYS.SCALP_UPGRADED_SKUS,
  CART_STORAGE_KEYS.INFLUENCER_UPSELL_SKUS,
  CART_STORAGE_KEYS.SCALP_ACCOUNT_UPGRADED_SKUS,
  CART_STORAGE_KEYS.PDP_UPGRADED_SKU,
];

/** Key used by HeaderCartIcon/CartAppIcon for cached cart (useLocalStorage("cart", null)). */
const CART_CACHE_KEY = "cart";

/**
 * Clear all cart-related data from sessionStorage, localStorage, and the cart cache key.
 * Call this when an order is placed successfully so the next session starts with a clean cart.
 */
export function clearAllCartStorage(): void {
  try {
    if (typeof window === "undefined") return;
    SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
    LOCAL_KEYS.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem(CART_CACHE_KEY);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Failed to clear cart storage:", error);
  }
}

/* Upgraded Items Helpers (sessionStorage) */

export type UpgradedItems = Record<string, string>;

/**
 * Save an upgraded item to sessionStorage for undo functionality
 * @param sku - The SKU of the upgraded item (will be normalized to base SKU)
 * @param dpaLink - The DPA link to restore the original item
 * @param qty - Quantity of items
 * @param purchaseSource - Optional purchase source query params
 */
export function saveUpgradedItem(
  sku: string,
  dpaLink: string,
  qty: number,
  purchaseSource: string = ""
): boolean {
  const existing = getCartStorage<UpgradedItems>(CART_STORAGE_KEYS.UPGRADED_ITEMS) ?? {};
  const normalizedSku = sku.split("-")[0]; // Consistent key format - use base SKU
  existing[normalizedSku] = `${dpaLink}|${qty}${purchaseSource}`;
  return setCartStorage(CART_STORAGE_KEYS.UPGRADED_ITEMS, existing);
}

/**
 * Get all upgraded items from sessionStorage
 */
export function getUpgradedItems(): UpgradedItems {
  return getCartStorage<UpgradedItems>(CART_STORAGE_KEYS.UPGRADED_ITEMS) ?? {};
}

/* Removed Item Helpers (sessionStorage) */

export interface RemovedItemData {
  url: string;
  qty: number;
}

/**
 * Save a removed item URL for undo functionality
 * @param undoUrl - The URL to re-add the item
 */
export function saveRemovedItem(undoUrl: string): boolean {
  return setCartStorage(CART_STORAGE_KEYS.REMOVED_ITEM, undoUrl);
}

/**
 * Get the removed item data if exists
 * The URL format has qty embedded in skus param: /dpa/add/tocart/?skus=SKU|qty&...
 * @returns Object with full url and qty, or null if not found
 */
export function getRemovedItem(): RemovedItemData | null {
  const value = getCartStorage<string>(CART_STORAGE_KEYS.REMOVED_ITEM);
  if (!value) return null;
  // qty is embedded in the URL after | in the skus param, extract it but keep full URL for redirect
  const parts = value.split("|");
  const qty = parseInt(parts[1] ?? "1", 10);
  return { url: value, qty: isNaN(qty) ? 1 : qty };
}

/**
 * Clear the removed item from storage (after undo or dismissal)
 */
export function clearRemovedItem(): void {
  removeCartStorage(CART_STORAGE_KEYS.REMOVED_ITEM);
}

/* Scalp Upsell Combo Helpers (sessionStorage) */

export type ScalpUpsellAddedItems = {
  hairSerum: boolean;
  shampoo: boolean;
};

/**
 * Save that a scalp upsell item was added this session
 * @param itemType - "hairSerum" or "shampoo"
 */
export function saveScalpUpsellAdded(itemType: "hairSerum" | "shampoo"): boolean {
  const existing = getCartStorage<ScalpUpsellAddedItems>(CART_STORAGE_KEYS.SCALP_UPSELL_ADDED) ?? {
    hairSerum: false,
    shampoo: false,
  };
  existing[itemType] = true;
  return setCartStorage(CART_STORAGE_KEYS.SCALP_UPSELL_ADDED, existing);
}

/**
 * Check if a scalp upsell item was added this session
 * @param itemType - "hairSerum" or "shampoo"
 */
export function wasScalpUpsellAdded(itemType: "hairSerum" | "shampoo"): boolean {
  const added = getCartStorage<ScalpUpsellAddedItems>(CART_STORAGE_KEYS.SCALP_UPSELL_ADDED);
  return added?.[itemType] ?? false;
}

/**
 * Check if ScalpUpsellCombo was interacted with this session
 */
export function wasScalpUpsellComboInteracted(): boolean {
  const added = getCartStorage<ScalpUpsellAddedItems>(CART_STORAGE_KEYS.SCALP_UPSELL_ADDED);
  return added?.hairSerum || added?.shampoo || false;
}

/**
 * Clear ScalpUpsellCombo interaction state (called when leaving cart page)
 */
export function clearScalpUpsellComboState(): void {
  removeCartStorage(CART_STORAGE_KEYS.SCALP_UPSELL_ADDED);
}

/* Upsell Tracking Helpers (localStorage) */

/**
 * Get upsell SKUs from localStorage
 * @param key - The specific upsell tracking key
 */
export function getUpsellSkus(key: CartStorageKey): string[] | null {
  return getCartStorage<string[]>(key, "local");
}

/**
 * Get a single upsell SKU string from localStorage
 * @param key - The specific upsell tracking key
 */
export function getUpsellSku(key: CartStorageKey): string | null {
  return getCartStorage<string>(key, "local");
}

/**
 * Filter out a SKU from upsell tracking array and update storage
 * If array becomes empty, removes the storage key entirely
 * @param key - The storage key
 * @param skus - Current array of SKUs
 * @param skuToRemove - SKU to filter out
 */
export function filterAndUpdateUpsellSkus(
  key: CartStorageKey,
  skus: string[],
  skuToRemove: string
): void {
  if (skus.length === 1 && skus[0] === skuToRemove) {
    removeCartStorage(key, "local");
    return;
  }

  const filtered = skus.filter((item: string) => item !== skuToRemove);
  setCartStorage(key, filtered, "local");
}

/**
 * Remove an upsell tracking key from localStorage
 * @param key - The storage key to remove
 */
export function removeUpsellTracking(key: CartStorageKey): void {
  removeCartStorage(key, "local");
}

/* Upsell Shown Tracking Helpers (sessionStorage) */

type UpsellShownTracked = Record<string, boolean>;

/**
 * Check if an upsell "shown" event has already been tracked this session
 * @param upsellId - Unique identifier for the upsell (e.g., "scalp-upsell-combo", "hair-serum-upsell")
 */
export function wasUpsellShownTracked(upsellId: string): boolean {
  const tracked = getCartStorage<UpsellShownTracked>(CART_STORAGE_KEYS.UPSELL_SHOWN_TRACKED);
  return tracked?.[upsellId] ?? false;
}

/**
 * Mark an upsell "shown" event as tracked for this session
 * @param upsellId - Unique identifier for the upsell
 */
export function markUpsellShownTracked(upsellId: string): boolean {
  const existing = getCartStorage<UpsellShownTracked>(CART_STORAGE_KEYS.UPSELL_SHOWN_TRACKED) ?? {};
  existing[upsellId] = true;
  return setCartStorage(CART_STORAGE_KEYS.UPSELL_SHOWN_TRACKED, existing);
}

/**
 * Save the diff results for the Message component
 */
export function saveCartUndoData(data: CartUndoData): boolean {
  return setCartStorage(CART_STORAGE_KEYS.CART_UNDO_DATA, data);
}

/**
 * Get the current undo data
 */
export function getCartUndoData(): CartUndoData | null {
  return getCartStorage<CartUndoData>(CART_STORAGE_KEYS.CART_UNDO_DATA);
}

/**
 * Clear undo data (call after undo or manual fix)
 */
export function clearCartUndoData(): void {
  removeCartStorage(CART_STORAGE_KEYS.CART_UNDO_DATA);
}
